'use no memo';

import {
  Action,
  Card,
  DataStatus,
  DeleteDialog,
  DropdownItem,
  DropdownItemProps,
  Upload,
  openDeleteDialog,
  openDropdown,
  useDialog,
} from 'platform/components';
import {Box, Center, Heading, Hide, Icon, Show, Space, VStack} from 'platform/foundation';
import {Lightbox, LightboxAction, useLightbox, useLightboxActionsFactory} from 'platform/lightbox';
import {css} from 'styled-components';

import {memo, useState} from 'react';

import {isNilOrEmpty, isPositive} from 'ramda-adjunct';

import {useGetParticipationQuery} from '@dms/api/core';
import {useGetVehicleAlbumPhotoListQuery, useGetVehicleQuery} from '@dms/api/sales';
import {EntityResourceIds} from '@dms/api/shared';
import i18n from '@dms/i18n';

import {RequiredTestIdProps, buildArray, suffixTestId} from 'shared/web';

import {usePermissions} from '../../hooks/usePermissions/usePermissions';
import {useVehicleAlbum} from '../../hooks/useVehicleAlbum';
import {prepareVehicleAlbumImageUrl, VEHICLE_ALBUM_IMAGE_SIZE} from './utils';
import {VehicleAlbumImage} from './VehicleAlbumImage';

interface VehicleAlbumProps extends RequiredTestIdProps {
  title: string;
  vehicleId: string;
  vehicleAlbumId: string;
  canUpload?: boolean;
  canDelete?: boolean;
  hideWhenEmpty?: boolean;
  onAlbumChange?: () => void;
}

const ALLOWED_MIME_TYPES_TO_UPLOAD = ['image/jpeg', 'image/png', 'image/jpg'].join(', ');

function VehicleAlbumComponent(props: VehicleAlbumProps) {
  const lightboxActionsFactory = useLightboxActionsFactory();

  const {data: vehicleParticipation} = useGetParticipationQuery({
    resourceId: EntityResourceIds.vehicle,
    recordId: props.vehicleId,
  });
  const {data: vehicle} = useGetVehicleQuery({vehicleId: props.vehicleId});

  const [canUploadVehicleAlbumPhotos, canDeleteVehicleAlbumPhoto] = usePermissions({
    permissionKeys: ['createVehicleAlbumPhotos', 'deleteVehicleAlbumPhoto'],
    scopes: {
      createVehicleAlbumPhotos: {
        participation: vehicleParticipation,
        branchId: vehicle?.branchId,
      },
      deleteVehicleAlbumPhoto: {
        participation: vehicleParticipation,
        branchId: vehicle?.branchId,
      },
    },
  });

  const [
    isLightboxDeleteDialogOpen,
    openLightboxDeleteDialog,
    closeLightboxDeleteDialog,
    {storage: imageToDeleteId},
  ] = useDialog<string>();

  const vehicleAlbum = useVehicleAlbum({
    vehicleAlbumId: props.vehicleAlbumId,
    vehicleId: props.vehicleId,
  });

  const {
    data: albumImages,
    isLoading: isLoadingAlbumImages,
    isError: hasAlbumImagesError,
  } = useGetVehicleAlbumPhotoListQuery(
    {
      vehicleId: props.vehicleId,
      vehicleAlbumId: props.vehicleAlbumId,
    },
    {skip: isNilOrEmpty(props.vehicleId) || isNilOrEmpty(props.vehicleAlbumId)}
  );

  const [lightboxControls, {onOpen}] = useLightbox(props.vehicleAlbumId);
  const [isUploadInProgress, setIsUploadInProgress] = useState(false);

  const isUploadAllowed = canUploadVehicleAlbumPhotos && props.canUpload;

  if (props.hideWhenEmpty && !isLoadingAlbumImages && !isPositive(albumImages?.length)) {
    return null;
  }

  const handleSetAsCover = (photoId: string) =>
    vehicleAlbum.actions.setImageAsCoverPhoto(photoId).then((result) => {
      if (result) {
        props.onAlbumChange?.();
      }
    });

  const handleUnsetCover = () =>
    vehicleAlbum.actions.unsetImageAsCoverPhoto().then((result) => {
      if (result) {
        props.onAlbumChange?.();
      }
    });

  const handleDeleteImage = (photoId: string) =>
    vehicleAlbum.actions.deleteImageFromAlbum(photoId).then((result) => {
      if (result) {
        props.onAlbumChange?.();
      }
    });

  const buildImageActionsContextMenu = (image: {id: string; isCoverPhoto: boolean}) => {
    const isProcessing = vehicleAlbum.processingImageIds.includes(image.id);

    return buildArray<DropdownItemProps>([])
      .whenNot(image.isCoverPhoto, () => ({
        label: i18n.t('general.actions.setAsCoverPhoto'),
        prefix: <Icon value="image/photo" />,
        isDisabled: isProcessing,
        onClick: () => handleSetAsCover(image.id),
      }))
      .when(image.isCoverPhoto, () => ({
        label: i18n.t('general.actions.unsetCoverPhoto'),
        prefix: <Icon value="image/photo" />,
        isDisabled: isProcessing,
        onClick: () => handleUnsetCover(),
      }))
      .when(canDeleteVehicleAlbumPhoto, () => ({
        label: i18n.t('general.actions.delete'),
        prefix: <Icon value="action/delete" />,
        isDisabled: isProcessing,
        onClick: () => handleDeleteImage(image.id),
      }));
  };

  return (
    <Card
      data-testid={suffixTestId('vehicleAlbum', props)}
      title={props.title}
      actions={buildArray<Action>().when(props.canDelete, {
        'data-testid': suffixTestId('vehicleAlbum-deleteAlbum', props),
        type: 'button',
        title: i18n.t('entity.vehicle.albums.delete'),
        variant: 'dangerGhost',
        leftIcon: 'action/delete',
        onClick: () => {
          openDeleteDialog({
            text: i18n.t('entity.vehicle.albums.deleteQuestion'),
            onConfirm: () =>
              vehicleAlbum.actions.deleteAlbum().then((result) => {
                if (result) {
                  props.onAlbumChange?.();
                }
              }),
          });
        },
      })}
    >
      <Hide when={isPositive(albumImages?.length) || props.canUpload}>
        <Center width="100%" height={VEHICLE_ALBUM_IMAGE_SIZE.height}>
          <VStack align="center">
            <Icon value="image/image_alt" color="text.tertiary" size={8} />
            <Space vertical={2} />
            <Heading size={5} color="tertiary">
              {i18n.t('entity.vehicle.albums.empty')}
            </Heading>
          </VStack>
        </Center>
      </Hide>
      <Hide when={!isPositive(albumImages?.length) && !props.canUpload}>
        <div
          css={css`
            display: grid;
            grid-template-columns: repeat(
              auto-fill,
              minmax(${({theme}) => theme.getSize(VEHICLE_ALBUM_IMAGE_SIZE.width)}, 1fr)
            );
            width: 100%;
            min-height: ${({theme}) => theme.getSize(VEHICLE_ALBUM_IMAGE_SIZE.height)};
            grid-gap: ${({theme}) => theme.getSize(3)};
          `}
        >
          <DataStatus isLoading={isLoadingAlbumImages} isError={hasAlbumImagesError}>
            {albumImages?.map((image, index) => (
              <VehicleAlbumImage
                key={image.id}
                src={prepareVehicleAlbumImageUrl(image.url)}
                isCoverPhoto={image.isCoverPhoto}
                isLoading={vehicleAlbum.processingImageIds.includes(image.id)}
                data-testid={suffixTestId(`vehicleAlbum-images-[${index}]`, props)}
                onClick={() => onOpen(index)}
                onContextMenu={(event) => {
                  openDropdown(
                    event,
                    <>
                      {buildImageActionsContextMenu(image).map((item) => (
                        <DropdownItem key={item.label as string} {...item} />
                      ))}
                    </>,
                    {id: 'vehicle-album-image-context-menu'}
                  );
                }}
              />
            ))}
            {vehicleAlbum.filePreview.list().map((preview, index) => (
              <VehicleAlbumImage
                data-testid={suffixTestId(`vehicleAlbum-imagePreviews-[${index}]`, props)}
                key={preview.id}
                src={preview.uri}
                isLoading
              />
            ))}
            <Show when={isUploadAllowed}>
              <Box width="100%" height="100%" ratio="4 / 3">
                <Upload
                  isDisabled={isUploadInProgress}
                  data-testid={suffixTestId('vehicleAlbum-upload', props)}
                  type="card"
                  size="default"
                  accept={ALLOWED_MIME_TYPES_TO_UPLOAD}
                  isMultiple
                  shouldValidateAccept
                  uploadIcon="image/photo_camera"
                  onStart={(file) => {
                    setIsUploadInProgress(true);
                    vehicleAlbum.filePreview.show(file);
                  }}
                  onSuccess={() => {
                    setIsUploadInProgress(false);
                    props.onAlbumChange?.();
                  }}
                  onError={(...args) => {
                    setIsUploadInProgress(false);
                    vehicleAlbum.handlers.handleUploadError(...args);
                  }}
                  customRequest={({file, onSuccess, onError}) => {
                    if (file instanceof File) {
                      vehicleAlbum.actions.addImageToAlbum({
                        file,
                        onSuccess,
                        onError,
                      });
                    }
                  }}
                />
              </Box>
            </Show>
          </DataStatus>
        </div>
      </Hide>
      <Lightbox
        data-testid={suffixTestId('vehicleAlbum-lightbox', props)}
        controls={lightboxControls}
        images={lightboxActionsFactory.addFullActions(
          (albumImages ?? []).map((image) => ({
            id: image.id,
            url: image.url,
            resizeUrl: image.url,
          })),
          (image) =>
            buildArray<LightboxAction>().when(canDeleteVehicleAlbumPhoto, {
              id: 'deleteBtn',
              icon: 'action/delete',
              onClick: () => openLightboxDeleteDialog(image.id),
            })
        )}
      />
      <DeleteDialog
        isOpen={isLightboxDeleteDialogOpen}
        onClose={closeLightboxDeleteDialog}
        onConfirm={() => {
          if (imageToDeleteId) {
            handleDeleteImage(imageToDeleteId);
          }
        }}
        isInLightbox
      />
    </Card>
  );
}

export const VehicleAlbum = memo(VehicleAlbumComponent);
